import {Injectable} from '@angular/core';
import {Observable, of} from "rxjs";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {environment} from "../../../../environments/environment";
import {catchError, map} from "rxjs/operators";
import {SnackBarService} from "../../../@core/services/snack-bar.service";
import {IPromoCodeReward, IPromoCodeRewardsMetadata} from "../interfaces/promo-code-reward.interface";
import {ICreatePromoCodeReward} from "../interfaces/create-promo-code-reward.interface";
import {IUpdatePromoCodeReward} from "../interfaces/update-promo-code-reward.interface";
import {IUpdatePromoCode} from "../interfaces/update-promo-code.interface";
import {IPromoCode, IPromoCodesMetadata} from "../interfaces/promo-code.interface";
import {IPromoCodesLogMetadata} from "../interfaces/promo-code-log.interface";
import {
  ICreatePromoCodeChain,
  IGetPromoCodeChains,
  IPromoCodeChain,
  IPromoCodeChainsMetadata,
  IUpdatePromoCodeChain,
} from "../interfaces";

@Injectable({
  providedIn: 'root'
})
export class PromoCodesService {

  constructor(private readonly http: HttpClient,
              private sb: SnackBarService
  ) {
  }

  public create(params: any): Observable<IPromoCode> {
    return this.http.post<IPromoCode>(`${environment.GATEWAY_URL}/promo`, params)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public updatePromoCode(params: IUpdatePromoCode): Observable<IPromoCode> {
    return this.http.post<IPromoCode>(`${environment.GATEWAY_URL}/promo/update-promocode`, params)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public activatePromoCodeManually(
    promoCodeLogId: number,
    userId: number,
    depositAmount: number
  ): Observable<void> {
    return this.http.post<void>(
      `${environment.GATEWAY_URL}/promo/activate-promocode/${promoCodeLogId}`,
      {userId, depositAmount}
    ).pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public getPromoCodes(pagination: {
    limit: number;
    offset: number
  }, params: any): Observable<IPromoCodesMetadata> {
    return this.http.post<IPromoCodesMetadata>(`${environment.GATEWAY_URL}/promo/get-promocodes`, {pagination, ...params})
      .pipe(
        map(result => ({
          ...result,
          promoCodes: result.promoCodes.map(promoCode => ({
            ...promoCode,
            date_added: this.parsePromoCodeDate(promoCode.date_added),
            activation_start_at: this.parsePromoCodeDate(promoCode.activation_start_at),
            activation_end_at: this.parsePromoCodeDate(promoCode.activation_end_at)
          }))
        })),
        catchError((error: HttpErrorResponse) => this.onHandleError(error))
      );
  }

  private parsePromoCodeDate(value: string | Date): Date;
  private parsePromoCodeDate(value: string | Date | null): Date | null;
  private parsePromoCodeDate(value: string | Date | null): Date | null {
    if (value == null) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    // Timestamp без зоны в ответе API хранит UTC; не интерпретируем его как время браузера.
    const utcTimestamp = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value)
      ? `${value.replace(' ', 'T')}Z`
      : value;
    return new Date(utcTimestamp);
  }

  public getPromoCodesLog(pagination: {
    limit: number;
    offset: number
  }, params: any): Observable<IPromoCodesLogMetadata> {
    return this.http.post<IPromoCodesLogMetadata>(`${environment.GATEWAY_URL}/promo/get-promocodes-log`, {pagination, ...params})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public getPromoCodeRewards(pagination: {
    limit: number;
    offset: number
  }, params: any): Observable<IPromoCodeRewardsMetadata> {
    return this.http.post<IPromoCodeRewardsMetadata>(`${environment.GATEWAY_URL}/promo/get-promocode-rewards`, {pagination, ...params})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public createPromoCodeReward(body: ICreatePromoCodeReward): Observable<IPromoCodeReward> {
    return this.http.post<IPromoCodeReward>(`${environment.GATEWAY_URL}/promo/create-promocode-reward`, body)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public updatePromoCodeReward(id: number, body: IUpdatePromoCodeReward): Observable<IPromoCodeReward> {
    return this.http.post<IPromoCodeReward>(`${environment.GATEWAY_URL}/promo/update-promocode-reward/${id}`, body)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public createPromoCodeChain(body: ICreatePromoCodeChain): Observable<IPromoCodeChain> {
    return this.http.post<IPromoCodeChain>(`${environment.GATEWAY_URL}/promo/create-promocode-chain`, body)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public updatePromoCodeChain(body: IUpdatePromoCodeChain): Observable<IPromoCodeChain> {
    const {id, ...params} = body;
    return this.http.post<IPromoCodeChain>(`${environment.GATEWAY_URL}/promo/update-promocode-chain/${id}`, params)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public getPromoCodeChains(pagination: {
    limit: number;
    offset: number;
  }, params: IGetPromoCodeChains = {}): Observable<IPromoCodeChainsMetadata> {
    return this.http.post<IPromoCodeChainsMetadata>(`${environment.GATEWAY_URL}/promo/get-promocode-chains`, {pagination, ...params})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  private onHandleError(error: HttpErrorResponse): Observable<any> {
    const message = error.error.statusCode + ' ' + (error.error.message || error.error.error);
    this.sb.openSnackBar(message, 3000, 'error');
    return of();
  }
}
